<template>
  <div>
    <v-card flat class="pa-3">
      <v-card-actions class="mb-1 pb-0">
        <div
          class="d-flex overflow-hidden card-rounded"
          style="border: 1px solid #ccc"
        >
          <v-text-field
            class="mx-1"
            v-model="search"
            prepend-inner-icon="search"
            :label="$t('common.Search')"
            single-line
            hide-details
            solo
            flat
          />
        </div>
        <v-spacer />
        <div>
          <v-select
            v-model="logtype"
            :loading="loading"
            label="Logbook"
            item-value="id"
            item-text="label"
            :items="logtypes"
            single-line
            hide-details
            solo
            class="thin-border"
          />
        </div>
      </v-card-actions>
      <v-data-table
        :loading="loading"
        :headers="headers"
        class="elevation-0"
        :items="displayableLogs"
        select-all
        :pagination.sync="pagination"
        :search="search"
        :filter="searchFilter"
        :custom-filter="customFilter"
      >
        <template v-slot:headers="props">
          <tr>
            <th
              v-for="header in props.headers"
              :key="header.text"
              :class="[
                'column sortable',
                pagination.descending ? 'desc' : 'asc',
                header.value === pagination.sortBy ? 'active' : ''
              ]"
              @click="changeSort(header.value)"
            >
              <v-icon small>arrow_upward</v-icon>
              {{ header.text }}
            </th>
          </tr>
        </template>
        <template v-slot:items="props">
          <tr>
            <td
              v-for="component in displayableComponents"
              :key="component.id"
              style="text-align: center"
              @click="() => displayLog(props.item.id)"
            >
              <span
                v-if="typeof props.item[component.id] === 'boolean'"
                class="ml-3"
              >
                <v-icon v-if="props.item[component.id]" small>check</v-icon>
              </span>
              <span v-else>{{ props.item[`${component.id}:text`] }}</span>
            </td>
          </tr>
        </template>
        <template v-if="fetchingLogs" v-slot:no-data>
          <tr style="visibility: hidden" />
        </template>
      </v-data-table>
    </v-card>
    <v-dialog
      v-model="showDisplayDialog"
      :persistent="true"
      :fullscreen="isMobile"
      :max-width="600"
      lazy
    >
      <EditForm
        :title="$t('common.ScientificActivity')"
        :logtypes="logtypes"
        :allcomponents="components"
        :close="closeDisplayLogForm"
        :logToEdit="logToDisplay"
        :updateMode="false"
      />
    </v-dialog>
  </div>
</template>
<script>
import { mapActions, mapGetters } from 'vuex'
import { Component, Option } from '@/models/Specialty'
import { ActivityLogbook } from '@/models/ScientificActivity'
import scientificActivityFetcher from '@/mixins/scientificActivityFetcher'
import EditForm from '@/views/ScientificActivity/components/EditForm'

export default {
  mixins: [scientificActivityFetcher],
  props: {
    coordinatedUser: {
      type: String
    }
  },
  components: {
    EditForm
  },
  data() {
    return {
      showDisplayDialog: false,
      logToDisplay: null,
      fetchingLogs: true,
      search: '',
      logtype: '',
      pagination: {
        sortBy: 'date', // TODO switch to entry-date when avaliable!
        rowsPerPage: 10
      }
    }
  },
  computed: {
    ...mapGetters(['coordinatiorScientificActivities']),
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown
    },

    components() {
      return Component.query().withAllRecursive().get() || []
    },
    loading() {
      return this.fetchingLogs || this.components.length === 0
    },
    logs() {
      const logs = this.coordinatiorScientificActivities || []
      return logs
    },
    logtypes() {
      return ActivityLogbook.query().get()
    },

    headers() {
      const headers = this.components
        .filter(c => c.show)
        .filter(c => c.logbooks && c.logbooks.includes(this.logtype))
        .map(({ label: text, id: value }) => ({
          text: this.$i18n.locale === 'pt' ? text : value,
          value,
          id: value
        }))
      return headers
    },
    logBookComponents() {
      return this.components.filter(c => c.logbooks.includes(this.logtype))
    },
    displayableComponents() {
      return this.logBookComponents.filter(c => c.show)
    },
    displayableLogs() {
      const logs = this.logs.map(log => {
        const displayableLog = { id: log.id }
        this.displayableComponents.forEach(c => {
          displayableLog[c.id] = log[c.id]
          displayableLog.logbook = log.logbook
          displayableLog[`${c.id}:text`] = this.getLabel(log, c)
        })
        displayableLog.userId = log.userId
        return displayableLog
      })
      return logs.filter(log => log.logbook === this.logtype)
    }
  },
  methods: {
    ...mapActions([
      'getCoordinatorScientificActivities',
      'setCoordinatorScientificActivities'
    ]),
    getLabel(log, component) {
      const item = log[component.id]
      const specialLabel = this.getSpecialLabel(log, component)
      if (specialLabel) {
        return specialLabel
      } else if (
        component.type === 'text-field' ||
        component.type === 'numeric-field' ||
        component.id === 'age'
      ) {
        return item
      } else if (component.type === 'text-area') {
        if (!item) return ''
        return item.length < 50 ? item : `${item.substring(0, 49)}...`
      } else if (component.type === 'date-picker') {
        return this.getDateLabel(item)
      } else if (typeof item === 'string') {
        return this.getOptionLabel(item)
      } else if (Array.isArray(item)) {
        return item.map(e => this.getOptionLabel(e)).join(', ')
      }
    },
    getDateLabel(javascriptTimeStamp = 'invalid') {
      const date = new Date(javascriptTimeStamp)
      let label = ''
      if (date.toString() !== 'Invalid Date') {
        label = date.toLocaleDateString(undefined, { timeZone: 'UTC' })
      }
      return label
    },
    getOptionLabel(id) {
      const option =
        Option.query()
          .where(opt => opt.value === id)
          .first() || {}
      return this.$i18n.locale === 'pt' ? option.text : option.value
    },

    getSpecialLabel(log, component) {
      if (component.id === 'authors') {
        const authorshipId = log.authorship || ''
        return (
          (
            Option.query()
              .where(
                el => el.component === 'authors' && el.value === authorshipId
              )
              .first() || {}
          ).text || ''
        )
      } else if (['journal-detail', 'book-detail'].indexOf(component.id) >= 0) {
        return log[component.id.replace('detail', 'name')]
      }
      return null
    },
    changeSort(column) {
      if (this.pagination.sortBy === column) {
        this.pagination.descending = !this.pagination.descending
      } else {
        this.pagination.sortBy = column
        this.pagination.descending = false
      }
    },
    customFilter(logs, pattern, filter) {
      return logs.filter(l => filter(l, pattern))
    },
    searchFilter(logRow, pattern) {
      let match = false
      if (!pattern) return true
      Object.values(logRow).forEach(val => {
        if (String(val).toLowerCase().includes(pattern.toLowerCase()))
          match = true
      })
      return match
    },
    displayLog(logId) {
      this.logToDisplay = this.logs.find(l => l.id === logId)
      this.showDisplayDialog = true
    },
    closeDisplayLogForm() {
      this.showDisplayDialog = false
      this.logToDisplay = null
    }
  },
  created() {
    this.fetchScientificActivity()
      .then(() => {
        if (this.logtypes.length && !this.logtype)
          this.logtype = this.logtypes[0].id
        if (this.coordinatedUser) {
          this.getCoordinatorScientificActivities([this.coordinatedUser])
            .catch(err =>
              this.showErrorSnackbar(err.message || 'Something went wrong')
            )
            .finally(() => (this.fetchingLogs = false))
        } else {
          this.fetchingLogs = false
        }
      })
      .catch(err =>
        this.showErrorSnackbar(err.message || 'Something went wrong')
      )
  },
  watch: {
    coordinatedUser(newUser) {
      this.fetchingLogs = true
      this.setCoordinatorScientificActivities([])
      this.getCoordinatorScientificActivities([newUser])
        .catch(err =>
          this.showErrorSnackbar(err.message || 'Something went wrong')
        )
        .finally(() => (this.fetchingLogs = false))
    },
    deep: true
  }
}
</script>
<style scoped>
td span {
  white-space: pre-line;
}
::v-deep .v-data-table__empty-wrapper {
  display: none;
}
</style>
